import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AdvancedNumberValueFormatter } from '../../../shared/helpers/number-formatter.helper';
import { PercentFormatter } from '../../../shared/helpers/percent-formatter.helper';
import { AllowedFormatsEnum } from '../../../shared/enums/common.enum';
import { GetExtensionFile } from '../../../shared/helpers/utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { COL_DEFS_SUPPLIER_INVOICES } from '../../common/constants/b-etr-supplier-invoices.columns.const';
import { COL_DEFS_INVOICE_DESCRIPTION } from '../../common/constants/d-etr-invoice-description.columns.const';
import {
  StatusEnum,
  VALIDATOR_ONLY_SPACES,
} from 'src/app/shared/constants/helpers.const';

@Component({
  selector: 'app-add-edit-contract',
  templateUrl: './add-edit-contract.component.html',
  styleUrls: ['./add-edit-contract.component.scss'],
})
export class AddEditContractComponent implements OnInit {
  gridOptions: any = {
    suppressMultiSort: true,
    suppressSorting: true,
    postSort: (rowNodes: any[]) => {
      rowNodes.reverse();
    },
  };

  isEditSupplierItem: boolean;
  isEditCustomerItem: boolean;
  supplierItemForm: FormGroup;
  customerItemForm: FormGroup;
  isNewProject: boolean;
  selectedSupplierItemIndex: any;
  selectedCustomerItemIndex: any;
  supplierTotal: number;
  supplierPercent: string;
  customerTotal: number;
  customerPercent: string;
  isShowTotalError: boolean;
  isShowSupplierTotalError: boolean;
  depositLimit: number;
  unit: string;
  @Input() currencyList: any[] = [];
  @Input() debtorList: any[] = [];
  contractForm: FormGroup;
  contractTotal: number;
  isShowLimitField: boolean;
  limit: number;
  isEditLimit: boolean;
  maxDate: Date;
  rowDataSupplierInvoices: any[] = [];
  rowDataInvoiceDescription: any[] = [];
  colDefsSupplierInvoices: ColDef[] = COL_DEFS_SUPPLIER_INVOICES(this);
  colDefsInvoiceDescription: ColDef[] = COL_DEFS_INVOICE_DESCRIPTION(this);
  defaultColDef: ColDef = {
    //flex: 1,
    resizable: false,
    floatingFilter: true,
    editable: true,
    rowDrag: false,
  };
  originatorList: { id: any; name: string }[] = [];
  debtorsList: { id: any; name: string }[] = [];
  gridApiSupplierInvoices: any;
  gridApiInvoiceDescription: any;
  paramsSupplierInvoices: any;
  paramsInvoiceDescription: any;
  contractStatusList = StatusEnum;
  contractStatus = this.contractStatusList.AWAITING;

  private deposit: number;
  private depositPercent: number;
  private fileName: string;

  @Input() contractData: any;
  @Output() closeDialog = new EventEmitter();
  @Output() contractDataEmitter = new EventEmitter();
  @Output() etrRequirementsEmitter: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    public activeModal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    this.isNewProject = !this.contractData;
    this.isShowLimitField = !this.contractData;
    if (this.isNewProject) {
      this.contractData = {
        b_etr: this.supplierTotal,
        d_etr: this.customerTotal,
        contractsData: {
          supplier: [],
          customer: [],
        },
      };
    }
    this.initForms(this.contractData);
    this.generateSupplierTotals();
    this.generateCustomerTotals();
    this.maxDate = new Date();
    this.getCurrencyList();
  }

  private getCurrencyList() {
    this.currencyList = this.currencyList.map((item) => ({
      id: item,
      name: item,
    }));
  }

  onGridReadySupplierInvoices(params: GridReadyEvent): void {
    this.gridApiSupplierInvoices = params.api;
    this.paramsSupplierInvoices = params;
    // this.onGridRefresh();
    this.setPinnedDataSupplierInvoices();
  }

  onGridReadyInvoiceDescription(params: GridReadyEvent): void {
    this.gridApiInvoiceDescription = params.api;
    this.paramsInvoiceDescription = params;
    // this.onGridRefresh();
    this.setPinnedDataInvoiceDescription();
  }

  setPinnedDataSupplierInvoices(value?: any): void {
    setTimeout(() => {
      const pinnedBottomData: any = [
        {
          supplierDate: 'Totals',
          amount: value,
        },
      ];
      this.gridApiSupplierInvoices.setGridOption(
        'pinnedBottomRowData',
        pinnedBottomData,
      );
    }, 500);
  }

  setPinnedDataInvoiceDescription(value?: any): void {
    setTimeout(() => {
      const pinnedBottomData: any = [
        {
          invoiceDate: 'Totals',
          amount: value,
        },
      ];
      this.gridApiInvoiceDescription.setGridOption(
        'pinnedBottomRowData',
        pinnedBottomData,
      );
    }, 500);
  }

  private initForms(data?: any): void {
    this.supplierItemForm = this.formBuilder.group({
      supDescription: [null],
      supValue: [null, [Validators.required, Validators.max(999999999)]],
      deposit: [null, [Validators.max(100)]],
    });

    this.customerItemForm = this.formBuilder.group({
      cusDescription: [null, [Validators.required]],
      cusValue: [null, [Validators.required, Validators.max(999999999)]],
      limit: [null, [Validators.max(100)]],
    });

    this.contractForm = this.formBuilder.group({
      debtor: [data?.debtor],
      originator: [data?.originator],
      contractFile: [null],
      description: [data?.description],
      startDate: [data?.startDate, [VALIDATOR_ONLY_SPACES]],
      endDate: [data?.endDate, [VALIDATOR_ONLY_SPACES]],
      approvedDate: [data?.approvedDate],
      approvedBy: [data?.approvedBy],
      reference: [data?.reference],
      projectName: [data?.projectName],
      ccy: [data?.ccy],
      total: [
        AdvancedNumberValueFormatter({ number: data?.contractTotal }),
        [Validators.required],
      ],
      limit: [PercentFormatter(+data.limitPercentage, false)],
    });

    this.limit = +data?.limitPercentage;
    this.deposit = +data?.deposit;
    this.depositPercent = +data?.depositPercent;
    this.depositLimit = +data?.depositLimit;
    this.contractTotal = data?.contractTotal || 0;

    this.subscribeToDepositPercentChange();
    this.subscribeToDepositValueChange();
    this.subscribeToContractTotalChange();
  }

  private subscribeToDepositPercentChange() {
    this.supplierItemForm
      .get('deposit')
      ?.valueChanges.pipe(
        debounceTime(400),
        filter((data) => !!data),
      )
      .subscribe((value) => {
        this.depositPercent = value;
        this.deposit = (+this.depositLimit * +value) / 100;
        this.supplierItemForm
          .get('supValue')
          ?.patchValue(this.deposit, { emitEvent: false });
      });
  }

  handleCloseDialog(): void {
    this.closeDialog.emit(false);
  }

  private subscribeToDepositValueChange() {
    this.supplierItemForm
      .get('supValue')
      ?.valueChanges.pipe(
        debounceTime(400),
        filter((data) => !!data),
      )
      .subscribe((value) => {
        this.deposit = value;
        this.depositPercent = (+value / +this.depositLimit) * 100;
        this.supplierItemForm
          .get('deposit')
          ?.patchValue(PercentFormatter(this.depositPercent, false), {
            emitEvent: false,
          });
      });
  }

  private subscribeToContractTotalChange() {
    this.contractForm
      .get('total')
      ?.valueChanges.pipe(
        debounceTime(400),
        filter((data) => !!data && !isNaN(data)),
      )
      .subscribe((value) => {
        this.contractTotal = this.convertISONumberToNormal(value);
      });
  }

  private generateSupplierTotals(currentValue = 0): void {
    this.supplierTotal =
      this.contractData?.contractsData?.supplier.reduce(
        (acc: any, currentValue: any) => acc + +currentValue.value,
        0,
      ) + currentValue;
    if (!this.isNewProject) {
      this.contractData.b_etr = this.supplierTotal;
    }
  }

  private generateCustomerTotals(currentValue = 0): void {
    this.customerTotal = 0;
    for (
      let i = 0;
      i < this.contractData?.contractsData?.customer.length;
      i++
    ) {
      this.customerTotal +=
        +this.contractData?.contractsData?.customer[i].value || 0;
    }

    this.customerTotal += currentValue;
    if (!this.isNewProject) {
      this.contractData.d_etr = this.customerTotal;
    }
  }

  supplierDataClick(item: any, index: number): void {
    if (this.selectedSupplierItemIndex === index) {
      this.selectedSupplierItemIndex = null;
      this.isEditSupplierItem = false;
      this.supplierItemForm.reset();
      return;
    }

    this.isEditSupplierItem = true;
    this.selectedSupplierItemIndex = index;

    this.supplierItemForm.patchValue({
      supDescription: item.label,
      supValue: item.value,
      deposit: null,
    });
  }

  removeSupplierLine(removedItem: any, index: number): void {
    this.contractData.contractsData.supplier.splice(index, 1);
    this.contractData.contractsData.supplier =
      this.contractData.contractsData.supplier.map(
        (item: any, index: number) => ({
          ...item,
          id: index + 1,
        }),
      );
    this.contractData.contractsData.supplier =
      this.contractData.contractsData.supplier.map(
        (item: any, index: number, arr: any) => ({
          ...item,
          id: arr.length - index,
        }),
      );

    this.generateSupplierTotals();
  }

  customerDataClick(item: any, index: number): void {
    if (this.selectedCustomerItemIndex === index) {
      this.selectedCustomerItemIndex = null;
      this.isEditCustomerItem = false;
      this.isShowLimitField = false;
      this.customerItemForm.reset();
      return;
    }

    this.isEditCustomerItem = true;
    this.selectedCustomerItemIndex = index;
    this.isShowLimitField = item.label === 'Limit';

    this.customerItemForm.patchValue({
      cusDescription: item.label,
      cusValue: item.value,
      limit: +item.percent,
    });
  }

  removeCustomerLine(deletedItem: any, index: number): void {
    this.contractData.contractsData.customer.splice(index, 1);
    this.contractData.contractsData.customer =
      this.contractData.contractsData.customer.map(
        (item: any, index: number) => ({
          ...item,
          id: index + 1,
        }),
      );
    this.contractData.contractsData.customer =
      this.contractData.contractsData.customer.map(
        (item: any, index: number, arr: any) => ({
          ...item,
          id: arr.length - index,
        }),
      );
    this.generateCustomerTotals();
  }

  addSupplierContract(): void {
    if (!this.supplierItemForm.value) return;
    const formatedValue = this.convertISONumberToNormal(
      `${this.supplierItemForm.value.supValue}`,
    );
    this.generateSupplierTotals(formatedValue);
    if (this.depositLimit < this.supplierTotal) {
      this.isShowSupplierTotalError = true;
      this.supplierTotal = this.supplierTotal - formatedValue;
      return;
    }

    this.isShowSupplierTotalError = false;
    this.contractData.contractsData.supplier.unshift({
      label: !this.contractData.contractsData.supplier.length
        ? 'Deposit'
        : this.supplierItemForm.value.supDescription,
      id: this.contractData.contractsData.supplier.length + 1,
      value: formatedValue,
      percent: this.calculationPercent(this.depositLimit, formatedValue),
      ...(this.fileName && { file: { name: this.fileName } }),
    });

    this.fileName = '';

    if (this.supplierItemForm.value.deposit) {
      this.depositPercent = +this.supplierItemForm.value.deposit;
    }

    this.supplierItemForm.reset();
    this.contractData.b_etr = this.supplierTotal;
  }

  saveSupplierItemChanges(): void {
    const formatedValue = this.convertISONumberToNormal(
      `${this.supplierItemForm.value.supValue}`,
    );
    this.contractData.contractsData.supplier[this.selectedSupplierItemIndex] = {
      ...this.contractData.contractsData.supplier[
        this.selectedSupplierItemIndex
      ],
      label: this.supplierItemForm.value.supDescription,
      value: formatedValue,
      percent: this.calculationPercent(this.depositLimit, formatedValue),
      ...(this.fileName && { file: { name: this.fileName } }),
    };
    this.fileName = '';

    this.generateSupplierTotals();

    if (this.depositLimit < this.supplierTotal) {
      this.isShowSupplierTotalError = true;
      // this.supplierTotal = this.supplierTotal - +this.supplierItemForm.value.supValue;
      return;
    }
    this.isShowSupplierTotalError = false;
    if (this.supplierItemForm.value.deposit) {
      this.depositPercent = +this.supplierItemForm.value.deposit;
    }
    this.selectedSupplierItemIndex = null;
    this.isEditSupplierItem = false;
    this.supplierItemForm.reset();
  }

  addCustomerContract(): void {
    if (!this.customerItemForm.value) return;
    if (this.isShowLimitField) {
      this.contractForm
        .get('limit')
        ?.patchValue(
          PercentFormatter(this.customerItemForm.value.limit, false),
        );
      this.limit = +this.customerItemForm.value.limit;
      this.isShowLimitField = false;
      this.contractData.limitPercentage = +this.limit;
      this.generateDepositLimit();
      return;
    }

    const formatedValue = this.convertISONumberToNormal(
      this.customerItemForm.value.cusValue,
    );
    this.generateCustomerTotals(formatedValue);
    if (this.contractTotal < this.customerTotal) {
      this.isShowTotalError = true;
      this.customerTotal = this.customerTotal - formatedValue;
      return;
    }

    this.isShowTotalError = false;
    this.contractData.contractsData.customer.unshift({
      label: this.customerItemForm.value.cusDescription,
      id: this.contractData.contractsData.customer.length + 1,
      value: formatedValue,
      percent: this.calculationPercent(this.contractTotal, formatedValue),
      ...(this.fileName && { file: { name: this.fileName } }),
    });

    this.fileName = '';

    if (+this.customerItemForm.value.limit) {
      this.limit = +this.customerItemForm.value.limit;
      this.generateDepositLimit();
    }
    this.customerItemForm.reset();
    this.contractData.d_etr = this.customerTotal;
  }

  private convertISONumberToNormal(isoNumber: string): number {
    return +isoNumber.replace(/,/g, '');
  }

  private generateDepositLimit(): void {
    this.depositLimit = (this.contractTotal * this.limit) / 100;
  }

  saveCustomerItemChanges(): void {
    if (this.isShowLimitField) {
      this.limit = +this.customerItemForm.value.limit;
      this.isShowLimitField = false;
      this.contractData.limitPercentage = this.limit;
      this.contractData.contractsData.customer[this.selectedCustomerItemIndex] =
        {
          ...this.contractData.contractsData.customer[
            this.selectedCustomerItemIndex
          ],
          label: 'Limit',
          percent: this.limit,
        };
      this.generateDepositLimit();
      return;
    }

    const formatedValue = this.convertISONumberToNormal(
      `${this.customerItemForm.value.cusValue}`,
    );
    this.contractData.contractsData.customer[this.selectedCustomerItemIndex] = {
      ...this.contractData.contractsData.customer[
        this.selectedCustomerItemIndex
      ],
      label: this.customerItemForm.value.cusDescription,
      value: formatedValue,
      percent: this.calculationPercent(this.contractTotal, formatedValue),
      ...(this.fileName && { file: { name: this.fileName } }),
    };

    this.fileName = '';

    this.generateCustomerTotals();
    if (this.contractTotal < this.customerTotal) {
      this.isShowTotalError = true;
      // this.customerTotal = this.customerTotal - +this.customerItemForm.value.cusValue;
      return;
    }

    /*if (+this.customerItemForm.value.limit) {
      this.limit = +this.customerItemForm.value.limit;
      this.generateDepositLimit();
    }*/

    this.isShowTotalError = false;
    this.selectedCustomerItemIndex = null;
    this.isEditCustomerItem = false;
    this.customerItemForm.reset();
  }

  saveContract(): void {
    this.contractData = {
      ...this.contractData,
      ...this.contractForm.value,
      contractTotal: this.contractTotal,
      supplierTotal: this.supplierTotal,
      customerTotal: this.customerTotal,
      deposit: this.deposit,
      depositPercent: this.depositPercent,
      depositLimit: this.depositLimit,
    };
    this.contractDataEmitter.emit(this.contractData);
  }

  private calculationPercent(projectTotal: number, lineValue: number): number {
    return (lineValue / projectTotal) * 100;
  }

  changeUIValue(value: string): string {
    const parts = value.split(' ');
    const number = parts[0]; // Convert the numeric part to a float
    const unit = parts[1];
    this.unit = parts[1];
    return number;
  }

  focusOut(): void {
    // this.contractTotal = +this.contractForm.value.total;
    this.contractForm
      .get('total')
      ?.patchValue(
        AdvancedNumberValueFormatter({ number: this.contractForm.value.total }),
      );
  }

  saveChangedLimit(): void {
    this.limit = +this.contractForm.value.limit;
    this.isEditLimit = false;
    this.contractData.limitPercentage = this.limit;
    this.generateDepositLimit();
  }

  /**
   * open file manager
   */
  public selectFile(id: number): void {
    this.fileInput.nativeElement.click();
  }

  fileChange(event: any): void {
    const file: File = event.target.files[0];
    const formatList = Object.keys(AllowedFormatsEnum).map(
      (key) => AllowedFormatsEnum[key as keyof typeof AllowedFormatsEnum],
    );
    const extension = GetExtensionFile(file);
    this.fileName = file.name;

    const reader = new FileReader();
    reader.readAsText(file);
  }

  getExtensionFile(file: any): string {
    return file.name.substring(file.name.lastIndexOf('.') + 1);
  }

  handleEtrRequirements() {
    this.etrRequirementsEmitter.emit(true);
  }

  private setSupplierInvoicesColDefs(): void {
    this.gridApiSupplierInvoices?.setColumnDefs(this.colDefsSupplierInvoices);
    this.gridApiSupplierInvoices?.refreshHeader();
  }

  addBEtr(): void {
    this.rowDataSupplierInvoices = [
      ...this.rowDataSupplierInvoices,
      {
        id: this.rowDataSupplierInvoices.length + 1,
        supplierDate: null,
        bEtrSupplierInvoices: null,
        amount: null,
      },
    ];
    this.gridApiSupplierInvoices.applyTransaction({
      add: [this.rowDataSupplierInvoices],
    });
  }

  addDEtr(): void {
    this.rowDataInvoiceDescription = [
      ...this.rowDataInvoiceDescription,
      {
        id: this.rowDataInvoiceDescription.length + 1,
        invoiceDate: null,
        dEtrInvoiceDescription: null,
        amount: null,
      },
    ];
    this.gridApiInvoiceDescription.applyTransaction({
      add: [this.rowDataInvoiceDescription],
    });
  }

  handleProcess() {
    this.handleCloseDialog();
  }

  handleEditingStop(event: any, type: 'supplier' | 'description') {
    if (event.column.colId !== 'amount') return;
    if (type === 'supplier') {
      //@todo need create function
      const total = this.rowDataSupplierInvoices.reduce(
        (acc: any, curr: any) => acc + curr.amount,
        0,
      );
      this.setPinnedDataSupplierInvoices(total);
    } else {
      const total = this.rowDataInvoiceDescription.reduce(
        (acc: any, curr: any) => acc + curr.amount,
        0,
      );
      this.setPinnedDataInvoiceDescription(total);
    }
  }

  closeContractDialog() {
    this.activeModal.dismiss();
  }

  customSearch(term: string, item: any): boolean {
    if (!term) {
      return true;
    }
    const searchTerm = term.toLowerCase();
    const nameMatches = item.name.toLowerCase().includes(searchTerm);
    const idMatches = item.id.toString().toLowerCase().includes(searchTerm);
    return nameMatches || idMatches;
  }
}
